/**
 * The account dialog (D157): who the player is, and the doors in — behind
 * one button in the control row, the map dialog's pattern (a native
 * `<dialog>` opened as it attaches, so "open" and "rendered" cannot
 * disagree).
 *
 * @remarks
 * This is the frame: the trigger, the modal, its heading and its Close.
 * What the frame holds — the doors, the account, the last door's word —
 * is {@link AccountPanelComponent}, which the handoff page draws without
 * any frame at all (D176). The dialog owns "is it open" and nothing about
 * the account; every prop it does not read is the panel's, passed through.
 *
 * @packageDocumentation
 */

import { useId, useState } from 'react';

import { ModalCornerCloseComponent } from '../modal-dialog/modal-corner-close-component.js';
import { showModalWhileMounted } from '../modal-dialog/show-modal-while-mounted.js';
import { AccountPanelComponent, type AccountPanelComponentProps } from './account-panel-component.js';

/** Props for {@link AccountDialogComponent}: the panel's, and whether to start open. */
export interface AccountDialogComponentProps extends AccountPanelComponentProps {
	/** Open on first render — for the one state the player arrives in (a link that did not open) rather than reaches. */
	readonly isInitiallyOpen?: boolean;
}

/**
 * Render the account trigger and its modal dialog.
 *
 * @param props - {@link AccountDialogComponentProps}
 * @returns The trigger button and the dialog it opens
 */
export const AccountDialogComponent = ({ isInitiallyOpen = false, ...panel }: AccountDialogComponentProps) => {
	const [isOpen, setIsOpen] = useState(isInitiallyOpen);
	const headingId = useId();
	const { account } = panel;

	return (
		<>
			<button
				aria-haspopup="dialog"
				aria-label={account === undefined ? 'Account — signed out' : `Account — signed in as ${account.email}`}
				className="account-dialog__trigger"
				onClick={() => {
					setIsOpen(true);
				}}
				type="button"
			>
				<AccountIcon />
				<span className="account-dialog__trigger-label">{account === undefined ? 'Sign in' : 'Account'}</span>
			</button>
			{isOpen ? (
				<dialog
					aria-labelledby={headingId}
					className="account-dialog"
					onClose={() => {
						setIsOpen(false);
					}}
					ref={showModalWhileMounted}
				>
					<div className="account-dialog__body">
						<h2 className="account-dialog__heading" id={headingId}>
							{account === undefined ? 'Sign in' : 'Your account'}
						</h2>
						<AccountPanelComponent {...panel} />
						<button
							className="account-dialog__close"
							onClick={() => {
								setIsOpen(false);
							}}
							type="button"
						>
							Close
						</button>
					</div>
					<ModalCornerCloseComponent label="Close account" />
				</dialog>
			) : null}
		</>
	);
};

/**
 * The trigger's key glyph. Decorative — the button is named by its label.
 *
 * @returns The icon
 */
const AccountIcon = () => (
	<svg
		aria-hidden="true"
		className="account-dialog__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<circle cx="8" cy="12" r="4" />
		<path d="M12 12h9" />
		<path d="M18 12v3" />
		<path d="M21 12v2" />
	</svg>
);
